/**
 * Email Parser for Review Comment Import
 *
 * Parses common email formats from permit review departments
 * and extracts structured data for review comment creation.
 *
 * Supported formats:
 * - Building department review notifications
 * - Plan reviewer emails with comment lists
 * - Permit portal notification emails
 * - Generic correction letters
 *
 * Part of Phase 3: Email parsing for automated comment import (Future)
 */

import type { ReviewComment, ReviewCommentSource, ReviewCommentCategory } from '@/types/jurisdiction';
import { categorizeComment, detectSource } from '@/hooks/useReviewComments';

export interface ParsedEmail {
  subject: string;
  from: string;
  date: string;
  body: string;
}

export interface ParsedReviewComment {
  originalText: string;
  source: ReviewCommentSource;
  sourceName?: string;
  reviewerName?: string;
  category: ReviewCommentCategory;
  reviewCycle: number;
  pageReference?: string;
  sheetReference?: string;
  confidence: 'high' | 'medium' | 'low';
  parserNotes?: string;
}

export interface EmailParseResult {
  success: boolean;
  comments: ParsedReviewComment[];
  metadata: {
    emailSubject?: string;
    emailFrom?: string;
    emailDate?: string;
    permitNumber?: string;
    applicationNumber?: string;
    reviewCycle: number;
    sourceDepartment?: string;
    reviewerName?: string;
  };
  rawText: string;
  errors: string[];
}

// Common patterns for extracting permit/application numbers
const PERMIT_NUMBER_PATTERNS = [
  /(?:permit|app(?:lication)?)\s*#?\s*:?\s*([\w-]+)/i,
  /(?:PRE|BLD|PER|APP)-?\d{4}-?\d+/i,
  /(?:case|file)\s*#?\s*:?\s*([\w-]+)/i,
];

// Common patterns for detecting review cycles
const REVIEW_CYCLE_PATTERNS = [
  /(\d)(?:st|nd|rd|th)\s+review/i,
  /review\s+(?:cycle|round)\s*:?\s*(\d)/i,
  /re-?review\s*#?\s*(\d)/i,
  /revision\s+(\d)/i,
];

// Common patterns for page/sheet references
const PAGE_REFERENCE_PATTERNS = [
  /(?:page|pg\.?)\s*(\d+)/gi,
  /(?:sheet|sht\.?)\s*([A-Z]-?\d+)/gi,
  /(?:drawing|dwg\.?)\s*([A-Z]\d+)/gi,
];

// Patterns for extracting individual comments from a list
const COMMENT_LIST_PATTERNS = [
  /^\s*[\d]+[.)]\s*(.+)$/gm,              // 1. Comment or 1) Comment
  /^\s*[-•]\s*(.+)$/gm,                    // - Comment or • Comment
  /^\s*[a-z][.)]\s*(.+)$/gm,               // a. Comment or a) Comment
  /^Comment\s*\d+:\s*(.+)$/gim,            // Comment 1: ...
  /^Item\s*\d+:\s*(.+)$/gim,               // Item 1: ...
];

// Common email senders from building departments
const KNOWN_DEPARTMENT_PATTERNS: Array<{ pattern: RegExp; source: ReviewCommentSource; name: string }> = [
  { pattern: /building/i, source: 'building_dept', name: 'Building Department' },
  { pattern: /fire\s*marshal/i, source: 'fire_marshal', name: 'Fire Marshal' },
  { pattern: /zoning/i, source: 'zoning', name: 'Zoning Department' },
  { pattern: /planning/i, source: 'zoning', name: 'Planning Department' },
  { pattern: /engineering/i, source: 'engineering', name: 'Engineering' },
  { pattern: /environmental/i, source: 'environmental', name: 'Environmental' },
  { pattern: /utilities/i, source: 'utilities', name: 'Utilities' },
  { pattern: /permit/i, source: 'building_dept', name: 'Permit Office' },
];

/**
 * Parses raw email text and extracts structured review comments
 */
export function parseEmailForComments(rawText: string): EmailParseResult {
  const errors: string[] = [];
  const comments: ParsedReviewComment[] = [];

  // Extract metadata
  const metadata = extractEmailMetadata(rawText);

  // Try to extract individual comments from the email body
  const extractedComments = extractCommentsFromBody(rawText, metadata);

  if (extractedComments.length === 0) {
    // If no structured comments found, treat the whole body as one comment
    const trimmedBody = cleanEmailBody(rawText);
    if (trimmedBody.length > 20) {
      comments.push(createParsedComment(trimmedBody, metadata, 'low'));
    } else {
      errors.push('No comments could be extracted from the email');
    }
  } else {
    comments.push(...extractedComments);
  }

  return {
    success: comments.length > 0,
    comments,
    metadata,
    rawText,
    errors,
  };
}

/**
 * Extracts metadata from the email text
 */
function extractEmailMetadata(rawText: string): EmailParseResult['metadata'] {
  const metadata: EmailParseResult['metadata'] = {
    reviewCycle: 1,
  };

  // Extract permit/application number
  for (const pattern of PERMIT_NUMBER_PATTERNS) {
    const match = rawText.match(pattern);
    if (match) {
      if (pattern.source.includes('permit')) {
        metadata.permitNumber = match[1] || match[0];
      } else {
        metadata.applicationNumber = match[1] || match[0];
      }
      break;
    }
  }

  // Extract review cycle
  for (const pattern of REVIEW_CYCLE_PATTERNS) {
    const match = rawText.match(pattern);
    if (match && match[1]) {
      metadata.reviewCycle = parseInt(match[1], 10);
      break;
    }
  }

  // Try to detect department from email content
  for (const dept of KNOWN_DEPARTMENT_PATTERNS) {
    if (dept.pattern.test(rawText)) {
      metadata.sourceDepartment = dept.name;
      break;
    }
  }

  // Extract subject line if present
  const subjectMatch = rawText.match(/^Subject:\s*(.+)$/im);
  if (subjectMatch) {
    metadata.emailSubject = subjectMatch[1].trim();
  }

  // Extract from line if present
  const fromMatch = rawText.match(/^From:\s*(.+)$/im);
  if (fromMatch) {
    metadata.emailFrom = fromMatch[1].trim();
  }

  // Extract date if present
  const dateMatch = rawText.match(/^Date:\s*(.+)$/im);
  if (dateMatch) {
    metadata.emailDate = dateMatch[1].trim();
  }

  // Try to extract reviewer name
  const reviewerPatterns = [
    /(?:reviewer|reviewed by|plan checker):\s*(.+)/i,
    /(?:signed|sincerely),?\s*\n+\s*(.+)/i,
  ];
  for (const pattern of reviewerPatterns) {
    const match = rawText.match(pattern);
    if (match && match[1]) {
      metadata.reviewerName = match[1].trim().split('\n')[0];
      break;
    }
  }

  return metadata;
}

/**
 * Extracts individual comments from the email body
 */
function extractCommentsFromBody(
  rawText: string,
  metadata: EmailParseResult['metadata']
): ParsedReviewComment[] {
  const comments: ParsedReviewComment[] = [];
  const body = cleanEmailBody(rawText);

  // Try each comment list pattern
  for (const pattern of COMMENT_LIST_PATTERNS) {
    const matches = [...body.matchAll(pattern)];
    if (matches.length >= 2) {
      // Found a list of comments
      for (const match of matches) {
        const commentText = match[1]?.trim();
        if (commentText && commentText.length > 10) {
          comments.push(createParsedComment(commentText, metadata, 'high'));
        }
      }
      break; // Stop after finding a pattern that works
    }
  }

  // If no list pattern matched, try splitting by double newlines
  if (comments.length === 0) {
    const paragraphs = body.split(/\n\s*\n/).filter(p => p.trim().length > 20);
    if (paragraphs.length > 1) {
      for (const para of paragraphs) {
        const trimmed = para.trim();
        // Skip signature blocks and standard email footers
        if (!isSignatureOrFooter(trimmed)) {
          comments.push(createParsedComment(trimmed, metadata, 'medium'));
        }
      }
    }
  }

  // Extract page/sheet references for each comment
  for (const comment of comments) {
    const pageMatch = comment.originalText.match(/(?:page|pg\.?)\s*(\d+)/i);
    if (pageMatch) {
      comment.pageReference = `Page ${pageMatch[1]}`;
    }

    const sheetMatch = comment.originalText.match(/(?:sheet|sht\.?)\s*([A-Z]-?\d+)/i);
    if (sheetMatch) {
      comment.sheetReference = sheetMatch[1];
    }
  }

  return comments;
}

/**
 * Creates a parsed comment with smart categorization
 */
function createParsedComment(
  text: string,
  metadata: EmailParseResult['metadata'],
  confidence: 'high' | 'medium' | 'low'
): ParsedReviewComment {
  const category = categorizeComment(text);
  const source = metadata.sourceDepartment
    ? detectSourceFromName(metadata.sourceDepartment)
    : detectSource(text);

  return {
    originalText: text,
    source,
    sourceName: metadata.sourceDepartment,
    reviewerName: metadata.reviewerName,
    category,
    reviewCycle: metadata.reviewCycle,
    confidence,
  };
}

/**
 * Cleans email body by removing headers, signatures, and quoted text
 */
function cleanEmailBody(rawText: string): string {
  let body = rawText;

  // Remove email headers
  body = body.replace(/^(From|To|Subject|Date|Cc|Bcc):\s*.+$/gim, '');

  // Remove quoted replies
  body = body.replace(/^>\s*.+$/gm, '');
  body = body.replace(/On .+ wrote:$/gim, '');

  // Remove common email disclaimers
  body = body.replace(/This email and any attachments are confidential.+$/gis, '');
  body = body.replace(/CONFIDENTIALITY NOTICE.+$/gis, '');

  // Remove excessive whitespace
  body = body.replace(/\n{3,}/g, '\n\n');

  return body.trim();
}

/**
 * Checks if text appears to be a signature or footer
 */
function isSignatureOrFooter(text: string): boolean {
  const signatureIndicators = [
    /^(sincerely|regards|thanks|best|cheers)/i,
    /^(sent from|get outlook)/i,
    /^\s*--\s*$/,
    /^(phone|tel|fax|email|website):/im,
    /confidential/i,
    /unsubscribe/i,
  ];

  return signatureIndicators.some(pattern => pattern.test(text));
}

/**
 * Converts a department name to ReviewCommentSource
 */
function detectSourceFromName(name: string): ReviewCommentSource {
  for (const dept of KNOWN_DEPARTMENT_PATTERNS) {
    if (dept.pattern.test(name)) {
      return dept.source;
    }
  }
  return 'building_dept';
}

/**
 * Formats parsed comments for preview
 */
export function formatParsedCommentsForPreview(result: EmailParseResult): string {
  if (!result.success) {
    return `Failed to parse email: ${result.errors.join(', ')}`;
  }

  const lines: string[] = [];

  if (result.metadata.permitNumber) {
    lines.push(`Permit: ${result.metadata.permitNumber}`);
  }
  if (result.metadata.sourceDepartment) {
    lines.push(`From: ${result.metadata.sourceDepartment}`);
  }
  lines.push(`Review Cycle: ${result.metadata.reviewCycle}`);
  lines.push(`Comments Found: ${result.comments.length}`);
  lines.push('');

  result.comments.forEach((comment, index) => {
    lines.push(`--- Comment ${index + 1} (${comment.confidence} confidence) ---`);
    lines.push(`Category: ${comment.category.replace('_', ' ')}`);
    if (comment.pageReference) lines.push(`Page: ${comment.pageReference}`);
    if (comment.sheetReference) lines.push(`Sheet: ${comment.sheetReference}`);
    lines.push(`Text: ${comment.originalText.substring(0, 200)}${comment.originalText.length > 200 ? '...' : ''}`);
    lines.push('');
  });

  return lines.join('\n');
}

export default parseEmailForComments;
